import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ── Types ─────────────────────────────────────────────────────────────────────

interface BowlerCandidate {
    CLAVE: number;
    FICHANAL: number;
    NOMBRECOMPLETO: string;
    NOMBRE: string;
    APELLIDOP: string;
    EMAIL: string;
}

interface Tournament {
    CLAVE: number;
    NOMBRE: string;
    FECHAINICIO: string;
    FECHAFIN: string;
    CVEBOLICHE?: number;
    NOMBREBOLICHE?: string;
    CIUDADBOLICHE?: string;
}

interface PackageRow {
    CVEPAQUETE: number;
    DESCRIPCION: string;
    COSTO: number;
    PACKAGETYPE: string;
}

interface SquadRow {
    CLAVE: number;
    TURNO: number;
    DIASEMANA_I: string;
    HORARIO_I: string;
    CVEEVENTO: number;
    EVENTNAME: string;
    JUGADO: number;
    SQUAD_PAGADO: number;
    LINEAS: number | null;
    MESA: string | null;
    LETRA: string | null;
    HCP: number | null;
    MISTERY: number;
    PROGRESSIVE: number;
    SUSPENDIDO: number;
}

interface Balance {
    COMPRADO: number;
    PAGADO: number;
    BALANCE: number;
}

interface TournamentDetail {
    packages: PackageRow[];
    balance: Balance;
    squads: SquadRow[];
}

interface EventScore {
    eventId: number;
    eventName: string;
    average: number | null;
    totalPins: number;
    totalGames: number;
    cuts?: {
        scratchTotal: number;
        totalWithHcp: number;
        gamesBowled: number;
        hcp: number | null;
        leyenda: string | null;
    }[];
}

// ── Helper ────────────────────────────────────────────────────────────────────

const formatCurrency = (n: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

const formatDate = (d: string) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return d; }
};

// ── Component ─────────────────────────────────────────────────────────────────

const AdminBowlerHistory: React.FC = () => {
    const navigate = useNavigate();

    // auth guard
    useEffect(() => {
        if (!localStorage.getItem('loggedInAdmin')) navigate('/admin');
    }, [navigate]);

    // ── Search state ──────────────────────────────────────────────────────────
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState<BowlerCandidate[]>([]);
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const searchRef = useRef<HTMLDivElement>(null);

    // ── Selected bowler ───────────────────────────────────────────────────────
    const [selectedBowler, setSelectedBowler] = useState<BowlerCandidate | null>(null);

    // ── Column 1: tournaments ─────────────────────────────────────────────────
    const [tournaments, setTournaments] = useState<Tournament[]>([]);
    const [tournamentsLoading, setTournamentsLoading] = useState(false);

    // ── Column 2: packages + schedule + balance ───────────────────────────────
    const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
    const [tournamentDetail, setTournamentDetail] = useState<TournamentDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // ── Column 3: scores ──────────────────────────────────────────────────────
    const [scores, setScores] = useState<EventScore[]>([]);
    const [scoresLoading, setScoresLoading] = useState(false);

    // ── Close dropdown on outside click / touch ─────────────────────────
    useEffect(() => {
        const handle = (e: MouseEvent | TouchEvent) => {
            if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handle as EventListener);
        document.addEventListener('touchstart', handle as EventListener, { passive: true });
        return () => {
            document.removeEventListener('mousedown', handle as EventListener);
            document.removeEventListener('touchstart', handle as EventListener);
        };
    }, []);

    // ── Search handler ────────────────────────────────────────────────────────
    const handleQueryChange = (val: string) => {
        setQuery(val);
        if (searchTimer.current) clearTimeout(searchTimer.current);
        if (!val.trim()) { setSuggestions([]); setShowDropdown(false); return; }
        searchTimer.current = setTimeout(async () => {
            setSuggestionsLoading(true);
            try {
                const r = await fetch(`${BACKEND_URL}/api/admin/bowler-history/search?q=${encodeURIComponent(val.trim())}`);
                const data = await r.json();
                setSuggestions(data || []);
                setShowDropdown(true);
            } catch { setSuggestions([]); }
            finally { setSuggestionsLoading(false); }
        }, 300);
    };

    // ── Select bowler ─────────────────────────────────────────────────────────
    const handleSelectBowler = async (b: BowlerCandidate) => {
        setSelectedBowler(b);
        setQuery(`${b.FICHANAL} — ${b.NOMBRECOMPLETO || `${b.NOMBRE} ${b.APELLIDOP}`.trim()}`);
        setShowDropdown(false);
        setSuggestions([]);
        setSelectedTournament(null);
        setTournamentDetail(null);
        setScores([]);

        setTournamentsLoading(true);
        try {
            const r = await fetch(`${BACKEND_URL}/api/admin/bowler-history/tournaments/${b.CLAVE}`);
            const data = await r.json();
            setTournaments(data || []);
        } catch { setTournaments([]); }
        finally { setTournamentsLoading(false); }
    };

    // ── Select tournament → load packages + scores ────────────────────────────
    const handleSelectTournament = async (t: Tournament) => {
        setSelectedTournament(t);
        setTournamentDetail(null);
        setScores([]);
        if (!selectedBowler) return;

        setDetailLoading(true);
        setScoresLoading(true);

        // Load packages+balance+squads and scores in parallel
        const [detailRes, scoresRes] = await Promise.allSettled([
            fetch(`${BACKEND_URL}/api/admin/bowler-history/packages/${selectedBowler.CLAVE}/${t.CLAVE}`).then(r => r.json()),
            fetch(`${BACKEND_URL}/api/admin/bowler-history/scores/${selectedBowler.CLAVE}/${t.CLAVE}`).then(r => r.json()),
        ]);

        if (detailRes.status === 'fulfilled') setTournamentDetail(detailRes.value);
        setDetailLoading(false);

        if (scoresRes.status === 'fulfilled') setScores(scoresRes.value || []);
        setScoresLoading(false);
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">

            {/* Header */}
            <div className="bg-indigo-700 text-white shadow-lg">
                <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center gap-4">
                    <button
                        onClick={() => navigate('/admin/dashboard')}
                        className="bg-white bg-opacity-20 hover:bg-opacity-30 p-2.5 rounded-xl transition"
                        aria-label="Back"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <div>
                        <h1 className="text-xl font-bold tracking-tight">Bowler History</h1>
                        <p className="text-indigo-200 text-sm">Search a bowler to view their full tournament history</p>
                    </div>
                </div>
            </div>

            {/* Search bar — compact, top-left */}
            <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-2">
                <div ref={searchRef} className="relative w-full max-w-sm">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                        Search bowler (TAT #, name, or email)
                    </label>
                    <div className="relative">
                        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                        </svg>
                        <input
                            type="text"
                            value={query}
                            onChange={e => handleQueryChange(e.target.value)}
                            onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
                            placeholder="e.g. 12345, Smith, john@email.com"
                            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-300 bg-white shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 transition"
                        />
                        {suggestionsLoading && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                <div className="w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                            </div>
                        )}
                    </div>

                    {/* Dropdown */}
                    {showDropdown && suggestions.length > 0 && (
                        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl max-h-64 overflow-y-auto">
                            {suggestions.map(b => (
                                <button
                                    key={b.CLAVE}
                                    onClick={() => handleSelectBowler(b)}
                                    className="w-full text-left px-4 py-2.5 hover:bg-indigo-50 active:bg-indigo-100 transition flex items-center gap-3"
                                >
                                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex-shrink-0">
                                        {b.FICHANAL}
                                    </span>
                                    <div className="min-w-0">
                                        <p className="font-semibold text-sm text-gray-900 truncate">
                                            {b.NOMBRECOMPLETO || `${b.NOMBRE} ${b.APELLIDOP}`.trim()}
                                        </p>
                                        <p className="text-xs text-gray-400 truncate">{b.EMAIL}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}

                    {showDropdown && !suggestionsLoading && suggestions.length === 0 && query.trim().length > 0 && (
                        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl px-4 py-3 text-sm text-gray-500">
                            No bowlers found
                        </div>
                    )}
                </div>
            </div>

            {/* Column layout */}
            {selectedBowler && (
                <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4">

                    {/* Bowler identity badge */}
                    <div className="mb-4 inline-flex items-center gap-3 bg-white border border-indigo-200 rounded-2xl px-5 py-3 shadow-sm">
                        <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-sm">
                            {selectedBowler.FICHANAL}
                        </div>
                        <div>
                            <p className="font-bold text-gray-900">
                                {selectedBowler.NOMBRECOMPLETO || `${selectedBowler.NOMBRE} ${selectedBowler.APELLIDOP}`.trim()}
                            </p>
                            <p className="text-xs text-gray-400">{selectedBowler.EMAIL}</p>
                        </div>
                    </div>

                    {/* Three columns */}
                    <div className="flex flex-col lg:flex-row gap-4 items-start pb-4 lg:overflow-x-auto">

                        {/* ── Column 1: Tournaments ─────────────────────────────── */}
                        <div className="w-full lg:flex-shrink-0 lg:w-72 bg-white rounded-2xl shadow-xl overflow-hidden">
                            <div className="bg-indigo-600 px-4 py-3">
                                <h2 className="text-white font-bold text-sm uppercase tracking-wide flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    Tournaments
                                </h2>
                                <p className="text-indigo-200 text-xs mt-0.5">{tournaments.length} found</p>
                            </div>

                            {tournamentsLoading ? (
                                <div className="flex justify-center py-10">
                                    <div className="w-6 h-6 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                                </div>
                            ) : tournaments.length === 0 ? (
                                <p className="text-gray-400 text-sm px-4 py-6 text-center">No tournament history found</p>
                            ) : (
                                <div className="divide-y divide-gray-100">
                                    {tournaments.map(t => (
                                        <button
                                            key={t.CLAVE}
                                            onClick={() => handleSelectTournament(t)}
                                            className={`w-full text-left px-4 py-3.5 transition hover:bg-indigo-50 ${
                                                selectedTournament?.CLAVE === t.CLAVE
                                                    ? 'bg-indigo-50 border-l-4 border-indigo-500'
                                                    : 'border-l-4 border-transparent'
                                            }`}
                                        >
                                            <p className="font-semibold text-sm text-gray-900 leading-tight">{t.NOMBRE}</p>
                                            <p className="text-xs text-gray-400 mt-0.5">
                                                {formatDate(t.FECHAINICIO)}
                                                {t.NOMBREBOLICHE ? ` · ${t.NOMBREBOLICHE}` : ''}
                                            </p>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* ── Column 2: Packages + Schedule + Balance ───────────── */}
                        {selectedTournament && (
                            <div className="w-full lg:flex-shrink-0 lg:w-80 bg-white rounded-2xl shadow-xl overflow-hidden">
                                <div className="bg-emerald-600 px-4 py-3">
                                    <h2 className="text-white font-bold text-sm uppercase tracking-wide flex items-center gap-2">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10" />
                                        </svg>
                                        Packages &amp; Schedule
                                    </h2>
                                    <p className="text-emerald-200 text-xs mt-0.5 truncate">{selectedTournament.NOMBRE}</p>
                                </div>

                                {detailLoading ? (
                                    <div className="flex justify-center py-10">
                                        <div className="w-6 h-6 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                                    </div>
                                ) : !tournamentDetail ? null : (
                                    <div className="overflow-y-auto lg:max-h-[70vh]">

                                        {/* Balance card */}
                                        <div className="m-4 rounded-xl bg-emerald-50 border border-emerald-200 p-4">
                                            <h3 className="text-xs font-bold uppercase tracking-wide text-emerald-700 mb-3">Balance</h3>
                                            <div className="grid grid-cols-3 gap-2 text-center">
                                                <div>
                                                    <p className="text-xs text-gray-500">Purchased</p>
                                                    <p className="font-bold text-gray-900 text-sm">{formatCurrency(tournamentDetail.balance.COMPRADO)}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs text-gray-500">Paid</p>
                                                    <p className="font-bold text-emerald-700 text-sm">{formatCurrency(tournamentDetail.balance.PAGADO)}</p>
                                                </div>
                                                <div>
                                                    <p className="text-xs text-gray-500">Balance</p>
                                                    <p className={`font-bold text-sm ${tournamentDetail.balance.BALANCE < 0 ? 'text-red-600' : tournamentDetail.balance.BALANCE === 0 ? 'text-emerald-700' : 'text-amber-600'}`}>
                                                        {formatCurrency(tournamentDetail.balance.BALANCE)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Packages */}
                                        {tournamentDetail.packages.length > 0 && (
                                            <div className="px-4 pb-3">
                                                <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">Packages</h3>
                                                <div className="space-y-2">
                                                    {tournamentDetail.packages.map((pkg, i) => (
                                                        <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                                                            <div className="min-w-0">
                                                                <p className="text-sm font-semibold text-gray-900 truncate">{pkg.DESCRIPCION}</p>
                                                                <p className="text-xs text-gray-400">{pkg.PACKAGETYPE}</p>
                                                            </div>
                                                            <span className="ml-2 flex-shrink-0 text-sm font-bold text-gray-700">{formatCurrency(pkg.COSTO)}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Schedule */}
                                        {tournamentDetail.squads.length > 0 && (
                                            <div className="px-4 pb-4">
                                                <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2 mt-3">Schedule</h3>
                                                {/* Group squads by event */}
                                                {Object.entries(
                                                    tournamentDetail.squads.reduce((acc: Record<string, SquadRow[]>, sq) => {
                                                        const key = sq.EVENTNAME || 'Unknown Event';
                                                        if (!acc[key]) acc[key] = [];
                                                        acc[key].push(sq);
                                                        return acc;
                                                    }, {})
                                                ).map(([evName, evSquads]) => (
                                                    <div key={evName} className="mb-3">
                                                        <p className="text-xs font-semibold text-indigo-700 mb-1">{evName}</p>
                                                        <div className="space-y-1.5">
                                                            {evSquads.map((sq, si) => (
                                                                <div key={si} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-xs">
                                                                    <div>
                                                                        <span className="font-semibold text-gray-800">{sq.DIASEMANA_I}</span>
                                                                        <span className="text-gray-400 ml-1">{sq.HORARIO_I}</span>
                                                                        {sq.MESA && <span className="ml-1 text-gray-400">· Lanes {sq.MESA}</span>}
                                                                        {sq.LETRA && <span className="ml-1 text-gray-400">({sq.LETRA})</span>}
                                                                    </div>
                                                                    <span className={`ml-2 px-1.5 py-0.5 rounded text-xs font-semibold ${
                                                                        sq.JUGADO === 2 ? 'bg-emerald-100 text-emerald-700' :
                                                                        sq.JUGADO === 1 ? 'bg-amber-100 text-amber-700' :
                                                                        'bg-gray-100 text-gray-500'
                                                                    }`}>
                                                                        {sq.JUGADO === 2 ? 'Bowled' : sq.JUGADO === 1 ? 'In Progress' : 'Upcoming'}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {tournamentDetail.packages.length === 0 && tournamentDetail.squads.length === 0 && (
                                            <p className="text-gray-400 text-sm px-4 py-6 text-center">No package or schedule data</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── Column 3: Scores ──────────────────────────────────── */}
                        {selectedTournament && (
                            <div className="w-full lg:flex-shrink-0 lg:w-96 bg-white rounded-2xl shadow-xl overflow-hidden">
                                <div className="bg-violet-600 px-4 py-3">
                                    <h2 className="text-white font-bold text-sm uppercase tracking-wide flex items-center gap-2">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                        </svg>
                                        Scores
                                    </h2>
                                    <p className="text-violet-200 text-xs mt-0.5">Per-event averages and squad results</p>
                                </div>

                                {scoresLoading ? (
                                    <div className="flex justify-center py-10">
                                        <div className="w-6 h-6 border-2 border-violet-400 border-t-transparent rounded-full animate-spin" />
                                    </div>
                                ) : scores.length === 0 ? (
                                    <p className="text-gray-400 text-sm px-4 py-6 text-center">No completed squads found for this tournament</p>
                                ) : (
                                    <div className="divide-y divide-gray-100 overflow-y-auto lg:max-h-[70vh]">
                                        {scores.map(ev => (
                                            <div key={ev.eventId} className="p-4">
                                                {/* Event header with average */}
                                                <div className="flex items-center justify-between mb-3">
                                                    <div>
                                                        <p className="font-bold text-gray-900 text-sm">{ev.eventName}</p>
                                                        <p className="text-xs text-gray-400">
                                                            {(ev.cuts || []).length} qualifying round{(ev.cuts || []).length !== 1 ? 's' : ''} · {ev.totalGames} game{ev.totalGames !== 1 ? 's' : ''}
                                                        </p>
                                                    </div>
                                                    {ev.average !== null ? (
                                                        <div className="text-right">
                                                            <p className="text-xs text-gray-400">Avg</p>
                                                            <p className="text-2xl font-black text-violet-700 leading-none">{ev.average.toFixed(1)}</p>
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-gray-400 italic">No scores yet</span>
                                                    )}
                                                </div>

                                                {/* Individual cut scores */}
                                                <div className="space-y-2">
                                                    {(ev.cuts || []).map((cut, ci) => (
                                                        <div key={ci} className="bg-gray-50 rounded-lg px-3 py-2 text-xs">
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-gray-500 font-medium">Round {ci + 1}</span>
                                                                <div className="flex items-center gap-3">
                                                                    <div className="text-right">
                                                                        <p className="text-gray-400 leading-none">Scratch</p>
                                                                        <p className="font-black text-gray-900 text-base leading-tight">{cut.scratchTotal.toLocaleString()}</p>
                                                                    </div>
                                                                    {cut.totalWithHcp !== cut.scratchTotal && cut.totalWithHcp > 0 && (
                                                                        <div className="text-right">
                                                                            <p className="text-gray-400 leading-none">+HCP</p>
                                                                            <p className="font-black text-violet-700 text-base leading-tight">{cut.totalWithHcp.toLocaleString()}</p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <p className="text-gray-400 mt-0.5">
                                                                {cut.gamesBowled} games
                                                                {cut.hcp ? ` · HCP ${cut.hcp}` : ''}
                                                                {cut.gamesBowled > 0
                                                                    ? ` · ${(cut.scratchTotal / cut.gamesBowled).toFixed(1)} avg`
                                                                    : ''}
                                                            </p>
                                                            {cut.leyenda && (
                                                                <p className="mt-1 text-indigo-600 font-semibold">{cut.leyenda}</p>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Event total */}
                                                {ev.totalGames > 0 && (
                                                    <div className="mt-3 pt-2 border-t border-gray-200 flex justify-between text-xs text-gray-500">
                                                        <span>Total pins: <strong className="text-gray-900">{ev.totalPins.toLocaleString()}</strong></span>
                                                        <span>Total games: <strong className="text-gray-900">{ev.totalGames}</strong></span>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Empty state before search */}
            {!selectedBowler && (
                <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16 flex flex-col items-center text-center">
                    <div className="bg-indigo-100 rounded-full p-6 mb-4">
                        <svg className="w-12 h-12 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                    </div>
                    <h2 className="text-xl font-bold text-gray-700 mb-1">Search for a bowler</h2>
                    <p className="text-gray-400 text-sm max-w-xs">Enter a TAT #, name, or email above to load their full history across all tournaments</p>
                </div>
            )}
        </div>
    );
};

export default AdminBowlerHistory;
